import {
  InquiryVersionSlot,
  InquiryWithContext,
} from '@texas/api/endpoints/inquiryApi';
import { useMemo } from 'react';

interface Params {
  mode: 'matrix' | 'non-matrix';
  versions: InquiryVersionSlot[] | null | undefined;
  inquiries: InquiryWithContext[] | null | undefined;
}

export function useFilterInquiries({ mode, versions, inquiries }: Params) {
  const allInquiries = useMemo(() => inquiries ?? [], [inquiries]);
  const allVersions = useMemo(() => versions ?? [], [versions]);

  const controllerVersionGroups = useMemo(() => {
    const slots = allVersions.filter(
      (v) =>
        v.controlledById === null &&
        (mode === 'matrix' ? v.isController : !v.isController),
    );

    const map = new Map<number, InquiryVersionSlot[]>();
    for (const slot of slots) {
      const existing = map.get(slot.version);
      if (existing) {
        existing.push(slot);
      } else {
        map.set(slot.version, [slot]);
      }
    }
    return map;
  }, [allVersions, mode]);

  const filteredInquiries = useMemo(
    () =>
      allInquiries.filter((inq) =>
        mode === 'matrix'
          ? inq.controlledById !== null
          : inq.controlledById === null,
      ),
    [allInquiries, mode],
  );

  const allTierColumns = useMemo(() => {
    const quantities = new Set<number>();
    for (const inq of filteredInquiries) {
      for (const tp of inq.tierPrices) quantities.add(tp.quantity);
      for (const tt of inq.allTargetTiers) quantities.add(tt.quantity);
    }
    return Array.from(quantities).sort((a, b) => a - b);
  }, [filteredInquiries]);

  const targetTierQuantities = useMemo(() => {
    const s = new Set<number>();
    for (const inq of filteredInquiries) {
      for (const tt of inq.allTargetTiers) s.add(tt.quantity);
    }
    return s;
  }, [filteredInquiries]);

  const hasAnyX = useMemo(
    () => filteredInquiries.some((inq) => inq.matrixNodeXValue),
    [filteredInquiries],
  );

  const hasAnyY = useMemo(
    () => filteredInquiries.some((inq) => inq.matrixNodeYValue),
    [filteredInquiries],
  );

  // Cell-level version slots that have no matching inquiry — shown as ghosted rows
  const missingCellSlots = useMemo(() => {
    if (mode !== 'matrix') return [];
    const coveredCgpiIds = new Set(
      allInquiries.map((inq) => inq.compositionGroupPurchaseInfoId),
    );
    return allVersions.filter(
      (v) =>
        v.controlledById !== null &&
        !coveredCgpiIds.has(v.compositionGroupPurchaseInfoId),
    );
  }, [mode, allVersions, allInquiries]);

  return {
    controllerVersionGroups,
    filteredInquiries,
    missingCellSlots,
    allTierColumns,
    targetTierQuantities,
    hasAnyX,
    hasAnyY,
  };
}
